import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Label } from '../ui/Label';
import { Input } from '../ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { Upload, FileText, X, Brain, Download, Info } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/Alert';
import toast from 'react-hot-toast';

const ImportModelModal = ({ isOpen, onClose, onImport }) => {
  const [file, setFile] = useState(null);
  const [format, setFormat] = useState('auto');
  const [importMode, setImportMode] = useState('create');
  const [filePreview, setFilePreview] = useState(null);
  const [errors, setErrors] = useState([]);
  const [status, setStatus] = useState('idle'); // idle, processing, success, error
  const [importOptions, setImportOptions] = useState({
    importVersions: true,
    importPerformance: true,
    validateData: true
  });

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setErrors([]);
      
      // Detect format
      const fileName = selectedFile.name.toLowerCase();
      if (fileName.endsWith('.csv')) {
        setFormat('csv');
      } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        setFormat('excel');
      } else if (fileName.endsWith('.json')) {
        setFormat('json');
      } else if (fileName.endsWith('.h5') || fileName.endsWith('.pkl') || fileName.endsWith('.pt') || fileName.endsWith('.onnx')) {
        setFormat('model');
      } else {
        setFormat('auto');
      }

      // Preview file (first few lines for CSV/Excel/JSON)
      if (fileName.endsWith('.csv')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const text = event.target.result;
          const lines = text.split('\n').slice(0, 5);
          setFilePreview({ type: 'csv', preview: lines });
        };
        reader.readAsText(selectedFile);
      } else if (fileName.endsWith('.json')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const json = JSON.parse(event.target.result);
            const preview = Array.isArray(json) ? json.slice(0, 2) : [json];
            setFilePreview({ type: 'json', preview: JSON.stringify(preview, null, 2) });
          } catch (e) {
            setFilePreview({ type: 'json', preview: 'Invalid JSON format' });
          }
        };
        reader.readAsText(selectedFile);
      } else if (fileName.endsWith('.h5') || fileName.endsWith('.pkl') || fileName.endsWith('.pt') || fileName.endsWith('.onnx')) {
        setFilePreview({ type: 'model', preview: `Model file: ${selectedFile.name} (${(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)` });
      }
    }
  };

  const handleImport = async () => {
    if (!file) {
      setErrors(['Please select a file to import']);
      return;
    }

    setStatus('processing');
    setErrors([]);

    try {
      const importData = {
        file,
        format,
        importMode,
        options: importOptions
      };
      
      await onImport(importData);
      setStatus('success');
      toast.success('Models imported successfully');
      
      // Reset after 2 seconds
      setTimeout(() => {
        setStatus('idle');
        setFile(null);
        setFilePreview(null);
        onClose();
      }, 2000);
    } catch (error) {
      setStatus('error');
      const errorMessage = error.message || 'Failed to import models';
      setErrors([errorMessage]);
      toast.error(errorMessage);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setFilePreview(null);
    setErrors([]);
  };

  const handleDownloadTemplate = () => {
    // Create CSV template
    const csvContent = [
      'name,description,type,version,framework,accuracy,precision,recall,f1Score,inferenceTime,trainingData,status',
      'Ticket Classification,Automatically classifies incoming tickets,Classification,v1.0.0,TensorFlow,94.2,94.2,92.8,93.5,45ms,12000 samples,active',
      'Sentiment Analysis,Analyzes customer sentiment,Sentiment Analysis,v1.0.0,PyTorch,89.7,89.7,87.3,88.5,32ms,8500 samples,active'
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'model-import-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded successfully');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Import AI Models"
      size="lg"
    >
      <div className="p-6 space-y-6">
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900 mb-1">Import Models</p>
            <p className="text-xs text-blue-700">
              Import AI models from files or upload pre-trained model files. Supported formats include CSV, Excel, JSON, and model files (.h5, .pkl, .pt, .onnx).
            </p>
          </div>
        </div>

        {/* Import Mode */}
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-gray-700">
            Import Mode
          </Label>
          <Select value={importMode} onValueChange={setImportMode}>
            <SelectTrigger className="border-gray-300">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="create">Create New Models Only</SelectItem>
              <SelectItem value="update">Update Existing Models</SelectItem>
              <SelectItem value="upsert">Create or Update (Upsert)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* File Selection */}
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-gray-700">
            Select File
          </Label>
          {!file ? (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors bg-gray-50">
              <div className="flex flex-col items-center">
                <div className="p-3 bg-blue-100 rounded-full mb-4">
                  <Brain className="h-8 w-8 text-blue-600" />
                </div>
                <p className="text-sm font-medium text-gray-900 mb-2">
                  Drag and drop a file here, or click to browse
                </p>
                <Input
                  type="file"
                  accept=".csv,.xlsx,.xls,.json,.h5,.pkl,.pt,.onnx"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="model-file-upload"
                />
                <label htmlFor="model-file-upload">
                  <Button variant="outline" type="button" className="cursor-pointer border-gray-300 hover:bg-gray-50">
                    <Upload className="h-4 w-4 mr-2" />
                    Browse Files
                  </Button>
                </label>
                <p className="text-xs text-gray-500 mt-3">
                  Supported formats: CSV, Excel (.xlsx, .xls), JSON, Model files (.h5, .pkl, .pt, .onnx)
                </p>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDownloadTemplate}
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download CSV Template
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg p-4 bg-white">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{file.name}</p>
                    <p className="text-xs text-gray-500">
                      {(file.size / 1024).toFixed(2)} KB • {format.toUpperCase()}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveFile}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* File Preview */}
              {filePreview && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-xs font-semibold text-gray-700 mb-2">File Preview:</p>
                  <div className="text-xs font-mono text-gray-700 overflow-x-auto max-h-32 overflow-y-auto">
                    {filePreview.type === 'json' ? (
                      <pre className="whitespace-pre-wrap">{filePreview.preview}</pre>
                    ) : filePreview.type === 'model' ? (
                      <p>{filePreview.preview}</p>
                    ) : (
                      filePreview.preview.map((line, index) => (
                        <div key={index} className="text-gray-700">{line}</div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Format Selection */}
        {file && (
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-gray-700">
              File Format
            </Label>
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger className="border-gray-300">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto-detect</SelectItem>
                <SelectItem value="csv">CSV</SelectItem>
                <SelectItem value="excel">Excel</SelectItem>
                <SelectItem value="json">JSON</SelectItem>
                <SelectItem value="model">Model File (.h5, .pkl, .pt, .onnx)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Import Options */}
        {file && format !== 'model' && (
          <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <Label className="text-sm font-semibold text-gray-700">
              Import Options
            </Label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={importOptions.importVersions}
                  onChange={(e) => setImportOptions({ ...importOptions, importVersions: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Import version history</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={importOptions.importPerformance}
                  onChange={(e) => setImportOptions({ ...importOptions, importPerformance: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Import performance metrics</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={importOptions.validateData}
                  onChange={(e) => setImportOptions({ ...importOptions, validateData: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Validate data before import</span>
              </label>
            </div>
          </div>
        )}

        {/* Errors */}
        {errors.length > 0 && (
          <Alert variant="destructive">
            <AlertDescription>
              <ul className="list-disc list-inside space-y-1">
                {errors.map((error, index) => (
                  <li key={index} className="text-sm">{error}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {/* Success Message */}
        {status === 'success' && (
          <Alert variant="success">
            <AlertDescription>
              <p className="font-medium">Models imported successfully!</p>
              <p className="text-sm mt-1">Your models have been imported and are ready to use.</p>
            </AlertDescription>
          </Alert>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <Button 
            variant="outline" 
            onClick={onClose} 
            disabled={status === 'processing'}
            className="border-gray-300 hover:bg-gray-50"
          >
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={!file || status === 'processing'}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
          >
            {status === 'processing' ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Processing...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                Import Models
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ImportModelModal;

